import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { deleteQueryLogs } from '../../api/delete-query-logs';
import { fetchQuery } from '../../api/fetch-query';
import { storeQueryLog } from '../../api/store-query-log';
import {
  IMessage,
  INlpRequest,
  IQuestionOption,
  ITopicGroup,
  ITopicTag,
  MessageType,
  NlpRequestType,
  TNullable,
  TopicStatus,
} from '../../common/types';
import ChatComposer from '../../components/chat/chat-composer';
import ChatMessageList from '../../components/chat/chat-message-list';
import ChatShell from '../../components/chat/chat-shell';
import LiveChatStatusBar from '../../components/chat/live-chat-status-bar';
import TopicSelector from '../../components/chat/topic-selector';
import { Button } from '../../components/ui/button';
import { HttpStatus } from '../../constants/http';
import { Pages } from '../../constants/pages';
import useChat from '../../hooks/use-chat';
import useConversation from '../../hooks/use-conversation';
import {
  convertQueryToMessage,
  convertResponseToMessage,
  findBestTopicSelectionMatchInQuery,
  getExactGroupFromTopicGroupsList,
  getExactTagFromTopicTagsList,
  getExactTopicFromTopicsList,
  getOnlyDisabledTopicFromGroup,
  getOnlyDisabledTopicFromTag,
  normalizeQueryForTopicMatching,
  QUERY_PARAM,
  questionOptionToMessage,
  SUBMIT_QUERY_PARAM,
  shouldUseMaxHeight,
  shouldUseSessionStorage,
  trimQuery,
} from '../../lib/utils';
import { CURRENT_INSTANCE } from '../../tenant';

function ChatScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    agreedToDisclaimer,
    agreedToDisclaimerPerSession,
    setShowAgreementBanner,
    topics,
    topicGroups,
    topicTags,
    selectedTopic,
    setSelectedTopic,
    messages,
    addMessage,
    addResponse,
    responses,
    resetChat,
    liveChat,
  } = useChat();
  const { conversationId } = useConversation();
  const [query, setQuery] = useState<string>('');
  const [topicGroup, setTopicGroup] = useState<TNullable<ITopicGroup>>(null);
  const [topicTag, setTopicTag] = useState<TNullable<ITopicTag>>(null);
  const [queryIncludesTopicFilter, setQueryIncludesTopicFilter] = useState<boolean>(false);
  const [pendingQueryLogId, setPendingQueryLogId] = useState<TNullable<string>>(null);
  const [request, setRequest] = useState<TNullable<INlpRequest>>(null);
  const [showTopicsError, setShowTopicsError] = useState<boolean>(false);
  const [showManualTopicPicker, setShowManualTopicPicker] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const messageCounterRef = useRef<number>(messages.length * 2);

  function buildTopicPickerUrl() {
    const params = new URLSearchParams();
    const trimmedQuery = trimQuery(query);

    if (trimmedQuery) {
      params.set(QUERY_PARAM, trimmedQuery);
    }

    return params.toString() ? `${Pages.TopicPicker}?${params.toString()}` : Pages.TopicPicker;
  }

  async function ensurePendingQueryLog(queryToLog: string) {
    if (pendingQueryLogId) {
      return;
    }

    const queryLog = await storeQueryLog(queryToLog);
    if (queryLog?.id) {
      setPendingQueryLogId(queryLog.id);
    }
  }

  async function clearPendingQueryLog() {
    if (!pendingQueryLogId) {
      return;
    }

    const queryLogId = pendingQueryLogId;
    setPendingQueryLogId(null);

    const isDeleted = await deleteQueryLogs([queryLogId]);
    if (!isDeleted) {
      console.error(`[chat-screen]: Failed to delete query log with id ${queryLogId}`);
    }
  }

  async function handleTopicSelect(topicId: string) {
    setShowTopicsError(false);
    setShowManualTopicPicker(false);
    const trimmedQuery = trimQuery(query);
    if (
      (shouldUseSessionStorage() && !agreedToDisclaimerPerSession) ||
      (!shouldUseSessionStorage() && !agreedToDisclaimer)
    ) {
      if (shouldUseMaxHeight()) {
        const params = new URLSearchParams({
          [QUERY_PARAM]: trimmedQuery,
          [SUBMIT_QUERY_PARAM]: 'true',
        });
        navigate(`${Pages.Disclaimer}?${params.toString()}`);
      } else {
        setShowAgreementBanner(true);
      }
      return;
    }
    const foundTopic = topics.find((topic) => topic.id === topicId);
    if (foundTopic?.status === TopicStatus.Disabled) {
      await clearPendingQueryLog();
      handleDisabledTopicResponse(trimmedQuery, foundTopic.displayName);
      return;
    }
    setSelectedTopic(topicId);
    if (topicGroup || topicTag) {
      handleStaticMessage(trimmedQuery, MessageType.UserEntry);
      if (foundTopic) {
        await clearPendingQueryLog();
        handleStaticMessage(
          t('chat.response.topic_changed', { topic: foundTopic.displayName }),
          MessageType.Message
        );
        if (queryIncludesTopicFilter) await handleQuerySend(trimmedQuery, topicId);
        else setQuery('');
      }
      return;
    }
    await clearPendingQueryLog();
    await handleQuerySend(trimmedQuery, topicId);
  }

  function addMessageDelayed(message: IMessage, delay: number = 1000) {
    setLoading(true);
    setTimeout(() => {
      addMessage(message);
      setLoading(false);
    }, delay);
  }

  function handleMoreAnswersShow(messageId: number) {
    const currentResponse = responses.find((item) => item.id === messageId);
    if (currentResponse) {
      const choices = currentResponse?.response.questionOptions;
      if (!choices) {
        return;
      }

      const leftOverChoices = choices.slice(1);
      addMessageDelayed(
        {
          id: ++messageCounterRef.current,
          message: t('chat.request.show_more'),
          type: MessageType.UserEntry,
        },
        0
      );
      addMessageDelayed(
        {
          id: ++messageCounterRef.current,
          message: t('chat.response.perhaps_one_of_these'),
          choices: leftOverChoices,
          type: MessageType.ChoiceResponse,
        },
        2000
      );
    }
  }

  async function handleChoiceSelect(choice: IQuestionOption | undefined) {
    if (!choice) {
      addMessageDelayed({
        id: ++messageCounterRef.current,
        type: MessageType.UserEntry,
        message: t('chat.request.contact_info_service'),
      });
      addMessageDelayed(
        { id: ++messageCounterRef.current, type: MessageType.ForceMedInfo, message: '' },
        2000
      );
      return;
    }
    addMessage({
      id: ++messageCounterRef.current,
      type: MessageType.UserEntry,
      message: t('chat.request.show_question') + choice.actualQuery,
    });
    const request: INlpRequest = {
      query: choice.actualQuery,
      topicId: selectedTopic,
      instanceId: CURRENT_INSTANCE.id,
      conversationId,
      queryType: NlpRequestType.ChoiceSelection,
    };

    setLoading(true);
    setRequest(request);
    const result = await fetchQuery(request);
    if (!result) {
      setLoading(false);
      return;
    }

    if (result.error) {
      if (result.status === HttpStatus.BadRequest) {
        handleStaticMessage('', MessageType.InvalidQuery);
        setLoading(false);
        return;
      }
      if (result.status === HttpStatus.NotFound) {
        addMessage(
          questionOptionToMessage(
            {
              status: 'infoservice',
              questionId: `random-${messageCounterRef.current}`,
              optionType: 'UNCERTAIN_OPTION',
              actualQuery: choice.actualQuery,
              answer: '',
              paraphrases: [],
              sourceDocuments: [],
              similarity: 1,
              references: [],
            },
            [],
            `random-${messageCounterRef.current}`,
            ++messageCounterRef.current
          )
        );
        addResponse(messageCounterRef.current, result);
        setLoading(false);
        return;
      }
      handleStaticMessage(result.error || t('common.general_error'), MessageType.Error);
      setLoading(false);
      return;
    }

    addMessage(
      questionOptionToMessage(choice, [], result.query?.id || '', ++messageCounterRef.current)
    );
    addResponse(messageCounterRef.current, result);
    setLoading(false);
  }

  function handleStaticMessage(message: string, type: MessageType) {
    messageCounterRef.current += 1;
    addMessage({
      message,
      type,
      id: messageCounterRef.current,
    });
  }

  function handleDisabledTopicResponse(query: string, topicName: string) {
    setQuery('');
    setLoading(false);
    handleStaticMessage(trimQuery(query), MessageType.UserEntry);
    handleStaticMessage(
      t('chat.response.disabled_topic', { topic: topicName }),
      MessageType.DisabledTopicMessage
    );
  }

  function handleManualTopicPickerOpen() {
    setTopicGroup(null);
    setTopicTag(null);
    setQueryIncludesTopicFilter(false);
    setShowTopicsError(false);
    if (CURRENT_INSTANCE.id === 'hexal') {
      navigate(buildTopicPickerUrl());
      return;
    }
    setShowManualTopicPicker(true);
  }

  function handleManualTopicPickerCancel() {
    setTopicGroup(null);
    setTopicTag(null);
    setQueryIncludesTopicFilter(false);
    setShowManualTopicPicker(false);
  }

  function handleManualTopicSelect(topicId: string) {
    const foundTopic = topics.find((topic) => topic.id === topicId);
    if (!foundTopic || foundTopic.status === TopicStatus.Disabled) {
      return;
    }

    setSelectedTopic(topicId);
    setTopicGroup(null);
    setTopicTag(null);
    setQueryIncludesTopicFilter(false);
    setShowManualTopicPicker(false);

    handleStaticMessage(
      t('chat.response.topic_changed', { topic: foundTopic.displayName }),
      MessageType.Message
    );
  }

  async function handleQuerySend(query: string, topicId: string, disabledTopicName?: string) {
    const trimmedQuery = trimQuery(query);
    if (!trimmedQuery) {
      setQuery('');
      return;
    }
    const topic = topics.find((topic) => topic.id === topicId);
    if (topic?.status === TopicStatus.Disabled) {
      handleDisabledTopicResponse(trimmedQuery, disabledTopicName ?? topic.displayName);
      return;
    }

    setLoading(true);
    const request: INlpRequest = {
      query: trimmedQuery,
      topicId,
      instanceId: CURRENT_INSTANCE.id,
      conversationId,
      queryType: NlpRequestType.Simple,
    };
    messageCounterRef.current += 1;
    addMessage(convertQueryToMessage(trimmedQuery, messageCounterRef.current));
    messageCounterRef.current += 1;
    setRequest(request);
    setQuery('');
    const result = await fetchQuery(request);
    if (result) {
      if (result.error) {
        if (result.status === HttpStatus.BadRequest) {
          handleStaticMessage('', MessageType.InvalidQuery);
          setLoading(false);
          return;
        }
        if (result.status === HttpStatus.NotFound) {
          messageCounterRef.current += 1;
          const message = questionOptionToMessage(
            {
              status: 'infoservice',
              questionId: `random-${messageCounterRef.current}`,
              optionType: 'UNCERTAIN_OPTION',
              actualQuery: trimmedQuery,
              answer: '',
              paraphrases: [],
              sourceDocuments: [],
              similarity: 1,
              references: [],
            },
            [],
            `random-${messageCounterRef.current}`,
            messageCounterRef.current
          );
          addMessage(message);
          addResponse(messageCounterRef.current, result);
          setLoading(false);
          return;
        }
        if (result.status === HttpStatus.InternalServerError) {
          handleStaticMessage(result.error || t('common.general_error'), MessageType.Error);
          setLoading(false);
          return;
        }
      }
      messageCounterRef.current += 1;
      const message = convertResponseToMessage(result, ++messageCounterRef.current);
      addMessage(message);
      addResponse(messageCounterRef.current, result);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (
      ((shouldUseSessionStorage() && agreedToDisclaimerPerSession) ||
        (!shouldUseSessionStorage() && agreedToDisclaimer)) &&
      query
    ) {
      const trimmedQuery = trimQuery(query);
      if (!trimmedQuery) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setQuery('');
        return;
      }
      const normalizedQuery = normalizeQueryForTopicMatching(trimmedQuery);
      if (!selectedTopic) {
        if (topicTags.length > 0) {
          const foundTopicTag = getExactTagFromTopicTagsList(topicTags, normalizedQuery);
          if (foundTopicTag) {
            const disabledTopicFromTag = getOnlyDisabledTopicFromTag(topics, foundTopicTag);
            if (disabledTopicFromTag) {
              handleDisabledTopicResponse(trimmedQuery, foundTopicTag.name);
              return;
            }
            void (async () => {
              await ensurePendingQueryLog(trimmedQuery);
              setTopicTag(foundTopicTag);
              setShowTopicsError(true);
            })();
            return;
          }
        }
        if (topicGroups.length > 0) {
          const foundTopicGroup = getExactGroupFromTopicGroupsList(topicGroups, normalizedQuery);
          if (foundTopicGroup) {
            const disabledTopicFromGroup = getOnlyDisabledTopicFromGroup(topics, foundTopicGroup);
            if (disabledTopicFromGroup) {
              handleDisabledTopicResponse(trimmedQuery, foundTopicGroup.name);
              return;
            }
            void (async () => {
              await ensurePendingQueryLog(trimmedQuery);
              setTopicGroup(foundTopicGroup);
              setShowTopicsError(true);
            })();
            return;
          }
        }
        const foundTopic = getExactTopicFromTopicsList(topics, normalizedQuery);
        if (foundTopic) {
          if (foundTopic.status === TopicStatus.Disabled) {
            handleDisabledTopicResponse(trimmedQuery, foundTopic.displayName);
            return;
          }
          handleStaticMessage(trimmedQuery, MessageType.UserEntry);
          setSelectedTopic(foundTopic.id);
          handleStaticMessage(
            t('chat.response.topic_changed', { topic: foundTopic.displayName }),
            MessageType.Message
          );
          setQuery('');
          return;
        }
        const topicSelectionMatch = findBestTopicSelectionMatchInQuery(
          trimmedQuery,
          topics,
          topicGroups,
          topicTags
        );
        if (topicSelectionMatch?.type === 'tag') {
          const matchedTopicTag = topicSelectionMatch.value;
          const disabledTopicFromTag = getOnlyDisabledTopicFromTag(topics, matchedTopicTag);
          if (disabledTopicFromTag) {
            handleQuerySend(trimmedQuery, disabledTopicFromTag.id, matchedTopicTag.name);
            setShowTopicsError(false);
            return;
          }
          void (async () => {
            await ensurePendingQueryLog(trimmedQuery);
            setTopicTag(matchedTopicTag);
            setQueryIncludesTopicFilter(true);
            setShowTopicsError(true);
          })();
          return;
        }
        if (topicSelectionMatch?.type === 'group') {
          const matchedTopicGroup = topicSelectionMatch.value;
          const disabledTopicFromGroup = getOnlyDisabledTopicFromGroup(topics, matchedTopicGroup);
          if (disabledTopicFromGroup) {
            handleQuerySend(trimmedQuery, disabledTopicFromGroup.id, matchedTopicGroup.name);
            setShowTopicsError(false);
            return;
          }
          void (async () => {
            await ensurePendingQueryLog(trimmedQuery);
            setTopicGroup(matchedTopicGroup);
            setQueryIncludesTopicFilter(true);
            setShowTopicsError(true);
          })();
          return;
        }
        if (topicSelectionMatch?.type === 'topic') {
          const matchedTopic = topicSelectionMatch.value;
          if (matchedTopic.status === TopicStatus.Disabled) {
            handleQuerySend(trimmedQuery, matchedTopic.id);
            setShowTopicsError(false);
            return;
          }
          setSelectedTopic(matchedTopic.id);
          handleQuerySend(trimmedQuery, matchedTopic.id);
          setShowTopicsError(false);
          return;
        }
        void (async () => {
          await ensurePendingQueryLog(trimmedQuery);
          setShowTopicsError(true);
        })();
        return;
      }
      if (topicGroup || topicTag) {
        handleStaticMessage(trimmedQuery, MessageType.UserEntry);
        const foundTopic = topics.find((topic) => topic.id === selectedTopic);
        if (foundTopic) {
          handleStaticMessage(
            t('chat.response.topic_changed', { topic: foundTopic.displayName }),
            MessageType.Message
          );
          if (queryIncludesTopicFilter) handleQuerySend(trimmedQuery, selectedTopic);
          else {
            setQuery('');
          }
        }
        return;
      }
      handleQuerySend(trimmedQuery, selectedTopic);
    }
  }, [agreedToDisclaimer, agreedToDisclaimerPerSession]); // eslint-disable-line

  const topicPickerControls = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="rounded-full border-slate-200/80 bg-white/90 px-3 py-1 text-xs text-slate-600 shadow-sm hover:border-slate-300 hover:bg-white hover:text-slate-900"
      onClick={handleManualTopicPickerOpen}
    >
      {selectedTopic ? t('common.change_topic') : t('common.select_topic')}
    </Button>
  );

  return (
    <div className="w-full flex justify-center">
      <div className="w-full max-w-4xl">
        <ChatShell
          addStaticMessage={handleStaticMessage}
          setQuery={setQuery}
          onResetChat={resetChat}
          topicPickerControls={topicPickerControls}
        >
          <ChatMessageList
            request={request}
            onShowMoreAnswers={handleMoreAnswersShow}
            onSelectChoice={handleChoiceSelect}
            isLoading={isLoading}
          />
          <LiveChatStatusBar />
          {showManualTopicPicker && (
            <TopicSelector
              topics={topics}
              topicGroup={null}
              selectTopic={handleManualTopicSelect}
              onCancel={handleManualTopicPickerCancel}
            />
          )}
          <ChatComposer
            query={query}
            setQuery={setQuery}
            setShowTopicsError={setShowTopicsError}
            topics={topics}
            topicGroups={topicGroups}
            topicTags={topicTags}
            setTopicGroup={setTopicGroup}
            setTopicTag={setTopicTag}
            setQueryIncludesTopicFilter={setQueryIncludesTopicFilter}
            addStaticMessage={handleStaticMessage}
            ensurePendingQueryLog={ensurePendingQueryLog}
            sendQuery={handleQuerySend}
            disabled={isLoading || showTopicsError || (liveChat.isActive && !liveChat.canSend)}
          />
          {showTopicsError && (
            <TopicSelector
              topics={topics}
              topicGroup={topicGroup}
              topicTag={topicTag}
              selectTopic={handleTopicSelect}
            />
          )}
        </ChatShell>
      </div>
    </div>
  );
}

export default ChatScreen;
